import { isObservable, Observable, of, shareReplay, switchMap } from 'rxjs';
import { Injector, isSignal, runInInjectionContext, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RawOrSignalOrObservable } from '../types/type.utils';

// Takes a raw value, Signal or Observable and returns an Observable
export function normalizeToObservable<T>(value: RawOrSignalOrObservable<T>) {
  if (isObservable(value)) {
    return value;
  } else if (isSignal(value)) {
    return toObservable(value);
  } else {
    return of(value);
  }
}

// Takes a raw value, Signal or Observable and returns a Signal
export function normalizeToSignal<T>(value: RawOrSignalOrObservable<T>) {
  if (isObservable(value)) {
    return toSignal(value);
  } else if (isSignal(value)) {
    return value;
  } else {
    return signal(value);
  }
}

/** Will take a factory that creates an observable and make sure it creates the observable lazyly on demand. This is used to allow the factory to run inside an injection context while also keeping rxjs Lazy observable style */
export function createLazyObservableWithInjectionContext<T>(
  injector: Injector,
  factory: () => Observable<T>,
  context?: object,
  share = true,
) {
  const observable = new Observable<Injector>(sub => sub.next(injector)).pipe(
    switchMap(injector => runInInjectionContext<Observable<T>>(injector, () => factory.call(context))),
  );
  return share ? observable.pipe(shareReplay(1)) : observable;
}

export function nextMicrotask<T>(value: T = undefined): Observable<T> {
  return new Observable<T>(sub => {
    queueMicrotask(() => sub.next(value));
  });
}

export function waitForNextMicrotask<T>() {
  return (observable: Observable<T>): Observable<T> => {
    return observable.pipe(switchMap(v => nextMicrotask(v)));
  };
}
